import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import { ModalController } from '@ionic/angular';
import { Router } from '@angular/router';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  finalize,
  map,
  startWith,
  switchMap,
  takeWhile,
  tap,
  timer,
} from 'rxjs';
import {
  CashFundsService,
  CashFundStatus,
  CashFundTransactionStatusEnum,
} from '@libs/core/api';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LoadingService, ToastService } from '@libs/ui/uikit';
import { ProjectsService } from '@front-end/core/services';

@UntilDestroy()
@Component({
  selector: 'front-end-fund-cash-payment-modal',
  templateUrl: './fund-cash-payment-modal.component.html',
  styleUrls: ['./fund-cash-payment-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FundCashPaymentModalComponent implements OnInit {
  private durationSeconds = 5 * 60;
  private timerRefreshInterval = 1000;
  private statusRefreshInterval = 10 * 1000;

  @Input() id?: number;

  radius = 60;
  circumference = 2 * Math.PI * this.radius;

  isActivatingCard$ = new BehaviorSubject(false);

  cardSpendingLimits$ = this.projectsService.selectedProject$.pipe(
    switchMap((project) =>
      this.cashFundService.getCardLimits({
        projectId: project.id as number,
      })
    )
  );
  refreshStatus$ = new BehaviorSubject<void>(undefined);
  status$ = new BehaviorSubject<CashFundStatus | null>(null);

  remaining$ = this.status$.pipe(
    filter((currentStatus) => currentStatus !== null),
    map((currentStatus) => currentStatus as CashFundStatus),
    switchMap((currentStatus) =>
      timer(0, this.timerRefreshInterval).pipe(
        startWith(0),
        map((i) => (currentStatus.remainingSeconds as number) - i),
        takeWhile((remaining) => remaining >= 0),
        map((remaining) => {
          return currentStatus.transactionStatus ===
            CashFundTransactionStatusEnum.Waiting
            ? remaining
            : 0;
        })
      )
    )
  );

  remainingString$ = this.remaining$.pipe(
    map((remaining) => {
      const minutes = Math.floor(remaining / 60)
        .toString()
        .padStart(2, '0');
      const seconds = (remaining % 60).toString().padStart(2, '0');
      return `${minutes}:${seconds}`;
    })
  );

  dashOffset$ = this.remaining$.pipe(
    map((percentage) => {
      return this.circumference * (1 - percentage / this.durationSeconds);
    })
  );

  expired$ = combineLatest([this.remaining$, this.status$]).pipe(
    filter(([remaining, status]) => remaining !== null && status !== null),
    map(
      ([remaining, status]) =>
        status?.transactionStatus !== CashFundTransactionStatusEnum.Failed &&
        (remaining === 0 ||
          status?.transactionStatus !== CashFundTransactionStatusEnum.Waiting)
    )
  );

  failed$ = this.status$.pipe(
    filter((status) => status !== null),
    map(
      (status) =>
        status?.transactionStatus === CashFundTransactionStatusEnum.Failed
    )
  );

  constructor(
    private loadingService: LoadingService,
    private modalController: ModalController,
    private router: Router,
    private cashFundService: CashFundsService,
    private toastService: ToastService,
    private projectsService: ProjectsService
  ) {}

  ngOnInit(): void {
    if (this.id === undefined) {
      this.modalController.dismiss();
      return;
    }

    this.loadingService.show(); // Show loader for first status request
    combineLatest([this.refreshStatus$, timer(0, this.statusRefreshInterval)])
      .pipe(
        switchMap(() =>
          this.cashFundService
            .getCashFundStatus({ cashFundId: this.id as number })
            .pipe(
              tap((status) => {
                if (
                  status.transactionStatus ===
                    CashFundTransactionStatusEnum.Success ||
                  status.transactionStatus ===
                    CashFundTransactionStatusEnum.Authorized
                ) {
                  this.toastService.success(
                    'pages.fund-cash.purchase.payment.success'
                  );
                  this.modalController.dismiss('paymentSuccessful');
                }
                this.status$.next(status);
                this.loadingService.hide();
              })
            )
        ),
        untilDestroyed(this)
      )
      .subscribe();

    this.refreshStatus$.next(undefined);
  }

  refreshCard() {
    if (!this.id) return;

    this.isActivatingCard$.next(true);
    this.cashFundService
      .activateCashFundCard({ cashFundId: this.id })
      .pipe(
        tap(() => {
          this.refreshStatus$.next(undefined);
        }),
        finalize(() => this.isActivatingCard$.next(false)),
        this.toastService.catchErrorWithToast(),
        untilDestroyed(this)
      )
      .subscribe();
  }

  close() {
    if (!this.id) return;

    this.cashFundService
      .cancelCashFundTransaction({ cashFundId: this.id })
      .pipe(
        finalize(() => {
          this.modalController.dismiss();
          this.router.navigateByUrl('/home/fund-cash/draft');
        }),
        this.toastService.catchErrorWithToast(),
        untilDestroyed(this)
      )
      .subscribe();
  }
}
